'use client';

import { useCallback, useSyncExternalStore } from 'react';

/** Tailwind's default screens (px). Keep in sync with any `--breakpoint-*` theme override. */
export const BREAKPOINTS = { sm: 640, md: 768, lg: 1024, xl: 1280 } as const;

export type Breakpoint = keyof typeof BREAKPOINTS;

/**
 * Where the shell switches from the hamburger drawer to the fixed sidebar.
 * `lg`, not `md`: a 256px rail at 768–1023 leaves a content column narrower
 * than a phone's, so tablets keep the drawer and a full-width page.
 */
export const SIDEBAR_BREAKPOINT: Breakpoint = 'lg';

/** The media query `useBreakpoint(bp)` listens to. */
export function breakpointQuery(bp: Breakpoint): string {
  return `(min-width: ${BREAKPOINTS[bp]}px)`;
}

/**
 * Whether `query` matches — the shared implementation behind the breakpoint
 * hooks below. The server render and the hydration pass both report
 * `serverValue` (desktop first by default, matching the SSR markup), so the
 * real answer only lands after mount. Export it if a page ever needs a query
 * the named hooks don't cover; prefer CSS wherever both variants can simply
 * coexist in the DOM.
 */
function useMediaQuery(query: string, serverValue = true): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    },
    [query]
  );
  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => serverValue
  );
}

/** True at `bp` and wider. Same SSR caveat as `useMediaQuery`. */
export function useBreakpoint(bp: Breakpoint): boolean {
  return useMediaQuery(breakpointQuery(bp));
}

/** Below `md`: phones and small tablets in portrait. Reports false only after mount. */
export function useIsMobile(): boolean {
  return !useBreakpoint('md');
}

/** True while the shell shows the fixed sidebar (and no mobile top bar / dock). */
export function useHasSidebar(): boolean {
  return useBreakpoint(SIDEBAR_BREAKPOINT);
}
